import { formatarValorMonetarioInputNumerico } from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { mensagemDeErroPadrao } from "shared/util/functions/yup/MensagensDeErroInput";
import { RegistroContaProvisionamentoSelectAsyncDTO } from "types/dto/contaProvisionamento/RegistroContaProvisionamentoSelectAsyncDTO";
import { RegistroPlanoContasSelectAsyncDTO } from "types/dto/contasPagar/RegistroPlanoContasSelectAsyncDTO";
import { RegistroFornecedorSelectAsyncDTO } from "types/dto/fornecedor/RegistroFornecedorSelectAsyncDTO";
import { RegistroNaturezaRendimentoSelectAsyncDTO } from "types/dto/naturezaRendimento/RegistroNaturezaRendimentoSelectAsyncDTO";
import { RegistroCondominioDTO } from "types/dto/RegistroCondominioDTO";
import { SelectOptionType } from "types/select/SelectOptionType";
import * as yup from "yup";
import { CadastrarDespesaRetencaoGroupPaySchema } from "./CadastrarDespesaRetencaoGroupPaySchema";
import { AnexoItem } from "shared/components/input/InputMultiAnexos";

const toNumber = (v: unknown): number => {
  if (v == null) return 0;
  const num = typeof v === "number" ? v : formatarValorMonetarioInputNumerico(String(v));
  return isNaN(num) ? 0 : num;
};

const somarValores = (
  itens: Record<string, unknown>[],
  key: "valorBruto" | "valor"
): number => itens.reduce((total, item) => total + toNumber(item[key]), 0);

const somarValorParcelas = (parcelas: Record<string, unknown>[]): number =>
  parcelas.reduce((acc, parcela) => acc + toNumber(parcela.valorParcela), 0);

export const retencaoMaiorQuePrimeiraParcela =
  "O valor da retenção não pode ser maior do que o valor da parcela.";

export const CadastrarDespesaParceladaGroupPaySchema = yup.object().shape({
  condominio: yup.mixed<SelectOptionType<RegistroCondominioDTO>>().required(mensagemDeErroPadrao("Condomínio")),

  valorTotal: yup
    .number()
    .transform((_, originalValue) => {
      let valor = originalValue ? originalValue : "0";
      return typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(valor);
    })
    .positive("O valor total deve ser maior que zero.")
    .required(mensagemDeErroPadrao("Valor total")),

  reterEmTodasParcelas: yup.boolean(),

  numeroDocumento: yup.string().nullable().notRequired(),

  serieNf: yup.string().nullable().notRequired(),

  parcelas: yup
    .array()
    .of(
      yup.object().shape({
        valorParcela: yup
          .number()
          .transform((_, originalValue) => {
            let valor = originalValue ? originalValue : "0";
            return typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(valor);
          })
          .positive("O valor parcelado deve ser maior do que 0!")
          .typeError("Informe um valor válido")
          .required(mensagemDeErroPadrao("Valor parcelado")),

        valorLiquido: yup
          .number()
          .transform((_, originalValue) => {
            let valor = originalValue ? originalValue : "0";
            return typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(valor);
          })
          .positive("O valor líquido deve ser maior do que 0!")
          .typeError("Informe um valor válido")
          .required(mensagemDeErroPadrao("Valor líquido")),

        dataVencimento: yup
          .date()
          .required(mensagemDeErroPadrao("Data de vencimento"))
          .typeError("Informe uma data válida"),

        mesCompetencia: yup
          .date()
          .required(mensagemDeErroPadrao("Mês competência"))
          .typeError("Informe uma data válida"),

        conta: yup.mixed<SelectOptionType<number>>().nullable(),

        formaPagamento: yup.mixed<SelectOptionType<string>>().nullable(),

        tipoFormaPagamentoPix: yup
          .string()
          .nullable()
          .when("formaPagamento", {
            is: (fp: SelectOptionType<string> | null) => fp?.value === "PIX",
            then: (schema) =>
              schema
                .required("Escolha uma forma de pagamento PIX")
                .oneOf(["TRANSFERENCIA", "QR_CODE"], "Selecione uma opção válida."),
          }),
        codigoCopiaColaPix: yup
          .string()
          .nullable()
          .when("tipoFormaPagamentoPix", {
            is: "QR_CODE",
            then: (schema) => schema.required(mensagemDeErroPadrao("Código Copia e Cola PIX")),
          }),
        numeroCheque: yup
          .string()
          .nullable()
          .when(["formaPagamento", "dataPagamento"], ([tipo, dataPagamento], schema) => {
            return tipo?.value === "CHEQUE" && !!dataPagamento
              ? schema.required(mensagemDeErroPadrao("Número do cheque"))
              : schema;
          }),
      })
    )
    .required()
    .test(
      "conta-primeira-parcela-com-data-pagamento",
      "Conta é obrigatória quando há data de pagamento na 1ª parcela.",
      function (this: any, parcelas: any[]) {
        const dataPagamento = this?.parent?.dataPagamento;
        if (!dataPagamento) return true;
        if (!Array.isArray(parcelas) || parcelas.length === 0) return true;
        const primeira = parcelas[0];
        if (!primeira?.conta) {
          return this.createError({
            path: `${this.path}[0].conta`,
            message: mensagemDeErroPadrao("Conta"),
          });
        }
        return true;
      }
    )
    .test(
      "forma-pagamento-primeira-parcela-com-data-pagamento",
      "Forma de pagamento é obrigatória quando há data de pagamento na 1ª parcela.",
      function (this: any, parcelas: any[]) {
        const dataPagamento = this?.parent?.dataPagamento;
        if (!dataPagamento) return true;
        if (!Array.isArray(parcelas) || parcelas.length === 0) return true;
        const primeira = parcelas[0];
        if (!primeira?.formaPagamento) {
          return this.createError({
            path: `${this.path}[0].formaPagamento`,
            message: mensagemDeErroPadrao("Forma de pagamento"),
          });
        }
        return true;
      }
    )
    .test(
      "numero-cheque-primeira-parcela-com-data-pagamento",
      "Número do cheque é obrigatório quando a forma de pagamento é cheque e há data de pagamento na 1ª parcela.",
      function (this: any, parcelas: any[]) {
        const dataPagamento = this?.parent?.dataPagamento;
        if (!dataPagamento) return true;
        if (!Array.isArray(parcelas) || parcelas.length === 0) return true;
        const primeira = parcelas[0];
        if (primeira?.formaPagamento?.value === "CHEQUE" && !primeira?.numeroCheque) {
          return this.createError({
            path: `${this.path}[0].numeroCheque`,
            message: mensagemDeErroPadrao("Número do cheque"),
          });
        }
        return true;
      }
    ),

  classesConta: yup
    .array()
    .of(
      yup.object().shape({
        classePlano: yup
          .mixed<SelectOptionType<RegistroPlanoContasSelectAsyncDTO>>()
          .required(mensagemDeErroPadrao("Classe de conta"))
          .test(
            "classe-plano-obrigatorio",
            mensagemDeErroPadrao("Classe de conta"),
            (opcao) => !!opcao?.value?.idClassePlano
          ),
        valorBruto: yup
          .number()
          .transform((_, originalValue) => {
            let valor = originalValue ? originalValue : "0";
            return typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(valor);
          })
          .required(mensagemDeErroPadrao("Valor bruto"))
          .typeError("Informe um valor válido.")
          .positive("O valor bruto deve ser maior do que 0!"),
        contaProvisionamento: yup
          .mixed<SelectOptionType<RegistroContaProvisionamentoSelectAsyncDTO>>()
          .nullable()
          .when("condominioPossuiContaProvisionamentoAtiva", {
            is: true,
            then: (schema) => {
              return schema.required(mensagemDeErroPadrao("Conta de provisionamento"));
            },
            otherwise: (schema) => schema,
          }),
        descricao: yup.string().max(150, "A descrição deve ter no máximo 150 caracteres.").nullable().notRequired(),
      })
    )
    .required(),

  fornecedor: yup
    .mixed<SelectOptionType<RegistroFornecedorSelectAsyncDTO>>()
    .required(mensagemDeErroPadrao("Fornecedor"))
    .test("fornecedor-obrigatorio", mensagemDeErroPadrao("Fornecedor"), (opcao) => {
      return !!opcao?.value?.nomeFornecedor || !!opcao?.value?.fornecedorId
    }),

  isFavorecidoFornecedor: yup.boolean(),

  favorecido: yup.mixed<SelectOptionType<RegistroFornecedorSelectAsyncDTO>>().when("isFavorecidoFornecedor", {
    is: true,
    then: (schema) =>
      schema
        .required(mensagemDeErroPadrao("Favorecido"))
        .test("favorecido-obrigatorio", mensagemDeErroPadrao("Favorecido"), (opcao) => !!opcao?.value?.fornecedorId),
  }),

  naturezaRendimento: yup.mixed<SelectOptionType<RegistroNaturezaRendimentoSelectAsyncDTO>>().nullable(),

  retencoes: yup
    .array()
    .of(CadastrarDespesaRetencaoGroupPaySchema)
    .required()
    .test(
      "retencaoMaiorQueValorParcelas",
      retencaoMaiorQuePrimeiraParcela,
      function (value) {
        if (!value?.length) return true;
        const parent = this.parent as {
          reterEmTodasParcelas?: boolean;
          parcelas?: Record<string, unknown>[];
        };

        const parcelas = parent?.parcelas ?? [];
        const somaRetencoes = somarValores(value as Record<string, unknown>[], "valor");
        const reterEmTodasParcelas = parent?.reterEmTodasParcelas === true;

        let limiteComparacao: number;
        if (reterEmTodasParcelas) {
          limiteComparacao = somarValorParcelas(parcelas);
        } else {
          const primeira = parcelas[0];
          if (!primeira) return true;
          limiteComparacao = toNumber(primeira.valorParcela);
        }

        return somaRetencoes <= limiteComparacao;
      }
    ),

  dataPagamento: yup
    .date()
    .nullable()
    .typeError("Informe uma data válida.")
    .when(["juros", "multa", "desconto"], ([valorJuros, valorMulta, valorDesconto], schema) => {
      const converterParaNumero = (valor: any) => (valor ? formatarValorMonetarioInputNumerico(String(valor)) : 0);
      const possuiAcrescimosOuDesconto =
        converterParaNumero(valorJuros) > 0 ||
        converterParaNumero(valorMulta) > 0 ||
        converterParaNumero(valorDesconto) > 0;
      return possuiAcrescimosOuDesconto ? schema.required(mensagemDeErroPadrao("Data de pagamento")) : schema;
    }),

  dataEmissao: yup.date().nullable().typeError("Informe uma data válida."),

  dataLiquidacao: yup
    .date()
    .nullable()
    .typeError("Informe uma data válida.")
    .when(["dataPagamento", "juros", "multa", "desconto"], ([dataPagamentoInformada, valorJuros, valorMulta, valorDesconto], schema) => {
      const converterParaNumero = (valor: any) => (valor ? formatarValorMonetarioInputNumerico(String(valor)) : 0);
      const possuiAcrescimosOuDesconto =
        converterParaNumero(valorJuros) > 0 ||
        converterParaNumero(valorMulta) > 0 ||
        converterParaNumero(valorDesconto) > 0;
      const schemaComObrigatoriedade =
        dataPagamentoInformada || possuiAcrescimosOuDesconto
          ? schema.required(mensagemDeErroPadrao("Data de liquidação"))
          : schema;
      return dataPagamentoInformada
        ? schemaComObrigatoriedade.min(
            yup.ref("dataPagamento"),
            "A data de liquidação não pode ser inferior à data de pagamento."
          )
        : schemaComObrigatoriedade;
    }),

  incluirAcrescimosDescontos: yup.boolean().notRequired(),
  juros: yup
    .number()
    .transform((_, val) => (val ? formatarValorMonetarioInputNumerico(String(val)) : 0))
    .nullable()
    .notRequired(),
  multa: yup
    .number()
    .transform((_, val) => (val ? formatarValorMonetarioInputNumerico(String(val)) : 0))
    .nullable()
    .notRequired(),
  desconto: yup
    .number()
    .transform((_, val) => (val ? formatarValorMonetarioInputNumerico(String(val)) : 0))
    .nullable()
    .notRequired(),
  valorPago: yup.number().nullable(),
  anexos: yup.mixed<AnexoItem[]>(),
});
